<template>
  <Bar :data="mapData" :options="chartOptions" />
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue';
import { Bar } from 'vue-chartjs';
import { ChartConfig } from './chartConfig';
import { Data, Series, SeriesConfig, SeriesOrderConfiguration } from '@/features/reports/models';
import ColourService from '@/shared/services/color';
import { ChartDataFormat } from '@/features/reports/models/chartDataFormat';
import type { ChartData, ChartOptions, TooltipItem } from 'chart.js';
import merge from 'lodash.merge'; // Import types for Chart.js 4

const props = defineProps<{
  data: Data;
  reportId: string;
  seriesOrder: SeriesOrderConfiguration;
  seriesToCompareTo?: string;
}>();

const renderSeriesInReverse = ref(true);

const orderedSeries = computed(() => {
  return seriesRenderOrder.value.map((seriesConfig) => getSeries(seriesConfig.name));
});

const seriesConfig = computed<SeriesConfig[]>(() => {
  if (!props.seriesOrder?.length) return [];
  if (typeof props.seriesOrder[0] === 'string') {
    return (props.seriesOrder as string[]).map((seriesName) => ({ name: seriesName, displayName: seriesName }));
  }
  return props.seriesOrder as SeriesConfig[];
});

const seriesRenderOrder = computed(() => {
  return renderSeriesInReverse.value ? [...seriesConfig.value].reverse() : seriesConfig.value;
});

const getSeries = (name: string): Series => {
  return props.data.series.find((series) => series.name === name)!;
};

const getSeriesConfig = (name: string): SeriesConfig => {
  return seriesConfig.value.find((config) => config.name === name) || { name, displayName: name };
};

const getIndexOfSeries = (name: string): number => {
  return seriesRenderOrder.value.findIndex((cfg) => cfg.name === name);
};

const mapData = computed<ChartData<'bar'>>(() => {
  const backgroundPalette = ChartConfig.borderPalette(seriesRenderOrder.value);
  const borderPalette = ChartConfig.borderPalette(seriesRenderOrder.value);

  return {
    labels: props.data.scale.labels,
    datasets: orderedSeries.value.map((series, index) => ({
      label: getSeriesConfig(series.name).displayName,
      data: props.seriesToCompareTo ? getAdjustedValues(series) : series.data,
      backgroundColor: backgroundPalette[index],
      borderColor: borderPalette[index],
      hoverBackgroundColor: ColourService.setAlpha(backgroundPalette[index], 0.4),
      borderWidth: 1,
    })),
  };
});

const getAdjustedValues = (seriesData: Series): number[] => {
  return seriesData.data.map((dataPoint, index) => {
    let value = dataPoint;
    value -= getValueForAdjustment(seriesData, index);
    return value;
  });
};

const getValueForAdjustment = (seriesData: Series, index: number) => {
  const thisSeriesIndex = getIndexOfSeries(seriesData.name);
  const adjustmentValueIsInSeriesAfter = getIndexOfSeries(props.seriesToCompareTo || '') === 0;

  if (adjustmentValueIsInSeriesAfter) {
    if (thisSeriesIndex < seriesRenderOrder.value.length - 1) {
      return orderedSeries.value[thisSeriesIndex + 1]?.data[index] || 0;
    }
  } else if (thisSeriesIndex > 0) {
    return orderedSeries.value[thisSeriesIndex - 1]?.data[index] || 0;
  }
  return 0;
};

const calculatePercentageLabels = computed(() => {
  const labels = new Map<string, string>();
  const compareToSeries = getSeries(props.seriesToCompareTo || '');
  const compareToTotal = compareToSeries.data.reduce((sum, value) => sum + value, 0);

  props.data.series.forEach((series) => {
    const total = series.data.reduce((sum, value) => sum + value, 0);
    const percentageOfTotal = compareToTotal === 0 ? 0 : (total / compareToTotal) * 100;
    const roundedPercentage = Math.round(percentageOfTotal * 100) / 100;
    labels.set(series.name, `${roundedPercentage}%`);
  });

  return labels;
});

const chartOptions = computed(() => {
  const overrides = {
    plugins: {
      stacked100: { enable: true, replaceTooltipLabel: false },
      inlineLabels: {
        reportId: props.reportId,
        reverseLabels: renderSeriesInReverse.value,
        dataFormat: ChartDataFormat.PERCENTAGE,
        defaultLabelValues: props.seriesToCompareTo ? calculatePercentageLabels.value : undefined,
      },
      crosshair: false,
      legend: {
        reverse: renderSeriesInReverse.value,
        position: 'right',
      },
      tooltip: {
        callbacks: {
          label: (tooltipItem: TooltipItem<'bar'>) => {
            const index = tooltipItem.dataIndex;
            const datasetIndex = tooltipItem.datasetIndex;

            const originalValue = orderedSeries.value[datasetIndex].data[index];
            const compareToValue = props.seriesToCompareTo
              ? getSeries(props.seriesToCompareTo).data[index]
              : orderedSeries.value.reduce((prev, curr) => prev + curr.data[index], 0);

            const percentageOfTotal = compareToValue === 0 ? 0 : (originalValue / compareToValue) * 100;
            const roundedPercentage = Math.round(percentageOfTotal * 100) / 100;

            return `${roundedPercentage}%`;
          },
        },
      },
    },
    scales: {
      x: {
        title: { display: true, text: props.data.scale.title },
        ticks: { maxTicksLimit: 32 },
        grid: { display: false },
      },
      y: {
        min: 0,
        max: 100,
        ticks: {
          callback: (value: any) => `${value}%`,
          stepSize: 10,
        },
      },
    },
  };

  return merge({}, ChartConfig.gridChartsGlobalOptions, overrides);
});
</script>
